import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, Loader2, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { fmt } from "@/components/shared";
import { CountUp, GlowCta } from "@/components/PlanVisuals";

const presets = [1, 5, 10, 50, 100];

export function QuantitySelector({ qty, setQty, max, testId = "share-qty" }) {
  const clamp = (v) => Math.max(1, Math.min(max || 1, Math.floor(Number(v) || 1)));
  return (
    <div data-testid={`${testId}-selector`}>
      <div className="flex items-center gap-2">
        <button type="button" data-testid={`${testId}-minus`} onClick={() => setQty(clamp(qty - 1))}
          className="w-11 h-11 rounded-xl glass glass-hover flex items-center justify-center text-cyan-300"><Minus className="w-4 h-4" /></button>
        <input data-testid={`${testId}-input`} type="number" min={1} max={max} value={qty} onChange={(e) => setQty(e.target.value === "" ? "" : clamp(e.target.value))}
          onBlur={() => setQty(clamp(qty))}
          className="flex-1 h-11 text-center bg-slate-900/60 border border-cyan-500/20 rounded-xl font-mono text-xl text-white focus:border-cyan-400 focus:outline-none" />
        <button type="button" data-testid={`${testId}-plus`} onClick={() => setQty(clamp(Number(qty || 0) + 1))}
          className="w-11 h-11 rounded-xl glass glass-hover flex items-center justify-center text-cyan-300"><Plus className="w-4 h-4" /></button>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {presets.map((p) => (
          <button type="button" key={p} data-testid={`${testId}-preset-${p}`} onClick={() => setQty(clamp(p))} disabled={p > max}
            className={`px-3 py-1 rounded-lg text-xs border transition-colors disabled:opacity-30 ${Number(qty) === p ? "bg-cyan-500/20 border-cyan-400/50 text-cyan-200" : "bg-cyan-500/10 border-cyan-500/20 text-cyan-300 hover:bg-cyan-500/20"}`}>{p} {p === 1 ? "share" : "shares"}</button>
        ))}
      </div>
    </div>
  );
}

export function SharePurchasePanel({ info, balance, onDone }) {
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const q = Number(qty) || 0;
  const total = q * info.share_price;
  const closed = !info.sales_enabled;
  const soldOut = info.available_shares < 1;
  const insufficient = total > balance;

  const buy = async () => {
    if (q < 1) return toast.error("Select at least 1 share");
    setLoading(true);
    try {
      const r = await api.post("/shares/purchase", { quantity: q });
      toast.success("Share purchase request submitted");
      onDone?.();
      nav(`/share-confirmation/purchase/${r.data.purchase.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="glass rounded-2xl p-6 space-y-5 relative overflow-hidden" data-testid="share-purchase-panel">
      <span className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-cyan-500/10 blur-3xl" aria-hidden="true" />
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg text-white">Buy Shares from Balance</h3>
        <span className="text-xs text-slate-400 font-mono">Balance {fmt(balance)} USDT</span>
      </div>
      <div className="flex items-end gap-2">
        <span className="font-mono text-3xl font-bold text-white" data-testid="share-price">{fmt(info.share_price)}</span>
        <span className="text-slate-400 mb-1">USDT per share</span>
      </div>
      <QuantitySelector qty={qty} setQty={setQty} max={info.available_shares} />
      <div className="bg-slate-900/60 rounded-xl p-4 border border-cyan-500/10 flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Total</p>
          <p className="text-[11px] text-slate-500 font-mono mt-0.5">{q} × {fmt(info.share_price)}</p>
        </div>
        <p className="font-mono text-2xl font-bold text-cyan-300" data-testid="share-total"><CountUp value={total} decimals={2} /> <span className="text-sm text-slate-500 font-sans">USDT</span></p>
      </div>
      {closed ? <p className="text-sm text-amber-300" data-testid="share-sales-closed">Share sales are currently closed.</p>
        : soldOut ? <p className="text-sm text-amber-300" data-testid="share-sold-out">All shares are currently allocated.</p>
        : insufficient && <p className="text-xs text-amber-300/80" data-testid="share-insufficient">Insufficient balance for this quantity — use "Deposit for Shares" below.</p>}
      <GlowCta testId="share-buy-btn" onClick={buy} disabled={loading || closed || soldOut || insufficient || q < 1} className="disabled:opacity-50 disabled:pointer-events-none">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Purchase {q} {q === 1 ? "Share" : "Shares"}
      </GlowCta>
      <p className="text-[11px] text-slate-500 flex items-start gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-cyan-400 shrink-0 mt-0.5" /> Funds are held and shares are reserved until an administrator approves the request. Rejected requests are refunded in full.</p>
    </div>
  );
}
